import '@fhs-legacy/frontend/src/polyfill';
import '@fhs-legacy/frontend/src/styles/theme';

import { useReactNavigationDevTools } from '@dev-plugins/react-navigation';
import { ErrorBoundaryProps, Stack, useNavigationContainerRef, usePathname } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { LogBox, Platform, Text, View } from 'react-native';

import { CartCommitLoading } from '@fhs/cart';
import { prefetchNativeHomepageHero } from '@fhs/home/src/native-homepage-hero/native-homepage-hero-query';
import { MqSwitch } from '@fhs/ui';
import AppWrapper from '@fhs-legacy/frontend/src/app';
import { AppContainer } from '@fhs-legacy/frontend/src/components/app-container';
import { useDeliveryClosedDialog } from '@fhs-legacy/frontend/src/hooks/use-delivery-closed-dialog';
import { PypestreamWrapper as Pypestream } from '@fhs-legacy/frontend/src/pages/pypestream';
import { isMobile, isSSG } from '@fhs-legacy/frontend/src/utils/environment';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { AppHeader } from '../components/app-header';
import { getScreenTitleHeader } from '../components/get-screen-title-header';
import { MaxCartAmountToast } from '../components/max-cart-amount-toast';
import { useOrderTypeFocusHandoff } from '../service-mode-dialog/order-type-focus-handoff';
import { getRewardsScreenOptions } from '../utils/get-rewards-screen-options';

const appHeaderDesktopOnly = () => (
  <MqSwitch $ltDesktop={() => null} $gteDesktop={() => <AppHeader />} />
);

const receiptStackHeader = getScreenTitleHeader({ backStyle: 'dynamic' });

// call this immediately per docs
// @see https://docs.expo.dev/versions/latest/sdk/splash-screen/#splashscreenpreventautohideasync
SplashScreen.preventAutoHideAsync();
// Native splash is a transparent image on black. The Lottie overlay matches
// that background, so hide without a fade once the overlay has laid out.
SplashScreen.setOptions({
  duration: 0,
  fade: false,
});
// We don't like logbox.
LogBox.ignoreAllLogs();
LogBox.uninstall();

// Warm the native homepage hero as early as possible
if (Platform.OS !== 'web') {
  void prefetchNativeHomepageHero();
}

export function ErrorBoundary(props: ErrorBoundaryProps) {
  return (
    <View style={{ flex: 1, marginTop: '20%', marginLeft: 8, marginRight: 8 }}>
      <Text style={{ textAlign: 'center', marginBottom: 16 }}>FATAL CRASH</Text>
      <Text style={{ fontWeight: 'bold', marginBottom: 16 }}>
        {props.error.name}: {props.error.message}
      </Text>
    </View>
  );
}

export const unstable_settings = {
  initialRouteName: '(tabs)',
};

function IndexLayout() {
  const isRewardsTiersEnabled = useFlag('enable-rewards-tiers');
  const pathname = usePathname();
  useOrderTypeFocusHandoff(pathname);

  return (
    <Stack initialRouteName="(tabs)" screenOptions={{ animation: 'slide_from_right' }}>
      <Stack.Screen
        name="os/[nativeAction]"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
      {/* Keep details at the root without changing /v2/offers/details/:offerId. */}
      <Stack.Screen
        name="(offer-details)/v2/offers/details/[offerId]"
        options={{
          headerShown: false,
          // The ActionSheet owns the animation; keep the page underneath stationary.
          presentation: 'transparentModal',
          contentStyle: { backgroundColor: 'transparent' },
          animation: 'none',
        }}
      />
      <Stack.Screen
        name="(offer-guide)/v2/offers/guide/[offerId]"
        options={{
          headerShown: false,
          // The ActionSheet owns the animation; keep the page underneath stationary.
          presentation: 'transparentModal',
          contentStyle: { backgroundColor: 'transparent' },
          animation: 'none',
        }}
      />
      <Stack.Screen
        name="(product)"
        options={{
          header: appHeaderDesktopOnly,
          headerShown: Platform.OS === 'web',
          presentation: 'card',
        }}
      />
      <Stack.Screen
        name="(auth)"
        options={{
          headerShown: false,
          presentation: 'modal',
        }}
      />
      <Stack.Screen
        name="(drawer)"
        options={{
          header: appHeaderDesktopOnly,
          presentation: 'card',
        }}
      />
      <Stack.Screen
        name="[staticPage]"
        options={{
          header: Platform.OS === 'web' ? () => <AppHeader /> : appHeaderDesktopOnly,
        }}
      />
      <Stack.Screen
        name="store-locator"
        options={{
          header: appHeaderDesktopOnly,
          animation: 'none',
        }}
      />
      <Stack.Screen name="menu" options={{ header: appHeaderDesktopOnly }} />
      <Stack.Screen
        name="cart"
        options={{
          headerShown: false,
          presentation: 'card',
        }}
      />
      <Stack.Screen
        name="order-confirmation"
        options={{
          header: appHeaderDesktopOnly,
          presentation: 'card',
        }}
      />
      <Stack.Screen
        name="receipt"
        options={{
          header: receiptStackHeader,
          title: 'Receipt',
          presentation: 'card',
        }}
      />
      <Stack.Screen
        name="redeem"
        options={{
          header: appHeaderDesktopOnly,
          ...(isRewardsTiersEnabled && {
            presentation: 'containedTransparentModal',
            contentStyle: { backgroundColor: 'transparent' },
            animation: 'slide_from_bottom',
          }),
        }}
      />
      <Stack.Screen
        name="rewards"
        options={({ route }) => getRewardsScreenOptions({ route }, appHeaderDesktopOnly)}
      />
      <Stack.Screen
        name="support"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="signout"
        options={{
          headerShown: false,
          presentation: 'card', // not formSheet — router.replace() leaves a ghost sheet (expo/expo#42836)
        }}
      />
      {/* TODO(simplified-menu): move this outside of the Provider stack */}
      <Stack.Screen
        name="auth"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="v2"
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="challenge/[challengeId]"
        options={{
          presentation: 'transparentModal',
          headerShown: false,
          contentStyle: { backgroundColor: 'transparent' },
          animation: 'none',
        }}
      />
      <Stack.Screen
        name="claim-points"
        options={{
          headerShown: false,
        }}
      />
    </Stack>
  );
}

function DeliveryClosedDialogHost() {
  return useDeliveryClosedDialog();
}

export default function RootLayout() {
  const navigationRef = useNavigationContainerRef();

  useReactNavigationDevTools(navigationRef);

  return (
    <AppWrapper>
      <AppContainer>
        <View nativeID="main-content" tabIndex={-1} style={{ flex: 1 }}>
          <IndexLayout />
        </View>
        <DeliveryClosedDialogHost />
        {!isSSG && !isMobile() && <Pypestream />}
        <MaxCartAmountToast />
        <CartCommitLoading />
      </AppContainer>
    </AppWrapper>
  );
}
